import React, { PureComponent } from "react";
import _ from "lodash";
import autoBind from "react-autobind";
import { Alert, Button, Checkbox, ControlLabel, FormControl, FormGroup, Label, Tab, Tabs } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	PROJECT_OPS_ACCOUNT_POLICIES,
	PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE,
	PROJECT_OPS_BUDGET_APPLICATION_POLICIES,
	PROJECT_OPS_CHANGE_MODES,
	PROJECT_OPS_ORIGINATION_MODES,
	PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
	PROJECT_OPS_TASK_WORK_ITEM_TYPE,
	getCustomHomeProjectTemplate,
	getProjectOpsTemplateBudgetTargetsText,
	normalizeProjectOpsBudgetTargets,
	normalizeProjectOpsTemplate
} from "../../../utils/projectOpsUtils";
import { getIchTrackedMilestones } from "../../../utils/ichMilestones";

const SELECT_BLANK = "";

const WORK_ITEM_TYPE_OPTIONS = [
	{ value: PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE, label: "Activity Run" },
	{ value: PROJECT_OPS_TASK_WORK_ITEM_TYPE, label: "Task" },
	{ value: PROJECT_OPS_PROJECT_WORK_ITEM_TYPE, label: "Project" }
];

const STATUS_OPTIONS = ["open", "blocked", "in_progress", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["normal", "low", "high", "critical"];
const ARTIFACT_TYPE_OPTIONS = ["", "contract", "addendum", "task"];
const CONTRACT_MODE_OPTIONS = ["", "initial_purchase", "lock_initial_purchase", "draft_addendum", "contract_change"];
const CHANGE_MODE_OPTIONS = [
	SELECT_BLANK,
	PROJECT_OPS_CHANGE_MODES.contractChange,
	PROJECT_OPS_CHANGE_MODES.budgetSettlement,
	PROJECT_OPS_CHANGE_MODES.hybrid
];
const BUDGET_POLICY_OPTIONS = [
	SELECT_BLANK,
	PROJECT_OPS_BUDGET_APPLICATION_POLICIES.explicit,
	PROJECT_OPS_BUDGET_APPLICATION_POLICIES.autoIfBudgetTarget,
	PROJECT_OPS_BUDGET_APPLICATION_POLICIES.autoIfSkuEligible
];
const ORIGINATION_MODE_OPTIONS = Object.values(PROJECT_OPS_ORIGINATION_MODES);
const ACCOUNT_POLICY_OPTIONS = Object.values(PROJECT_OPS_ACCOUNT_POLICIES);
// The ICH milestone this stage stamps on the contract anchor when it completes.
const MILESTONE_KEY_OPTIONS = [
	{ value: SELECT_BLANK, label: "(none)" },
	...getIchTrackedMilestones().map((milestone) => ({ value: milestone.key, label: `${milestone.label} (${milestone.key})` }))
];

function toJson(value) {
	return JSON.stringify(value || {}, null, 4);
}

function normalizeText(value) {
	return `${value || ""}`.trim();
}

function parseTemplateContent(content) {
	if (!content || (_.isString(content) && _.isEmpty(content))) {
		return getCustomHomeProjectTemplate();
	}
	if (_.isString(content)) {
		return normalizeProjectOpsTemplate(JSON.parse(content));
	}
	return normalizeProjectOpsTemplate(content);
}

function optionLabel(value) {
	return `${value || ""}`
		.replace(/^project_ops\./, "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, letter => letter.toUpperCase());
}

function textToBudgetTargets(value) {
	return normalizeProjectOpsBudgetTargets(`${value || ""}`.split(/\n/).map(line => line.trim()).filter(Boolean));
}

function slugify(value) {
	return normalizeText(value)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");
}

export default class ProjectOpsTemplateEditor extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);

		const template = this.parseContentWithFallback(props.content);
		const jsonText = toJson(template);
		this.lastEmittedContent = jsonText;
		this.state = {
			activeTab: "visual",
			template,
			jsonText,
			jsonError: ""
		};

		this.emitChange(jsonText, true);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.content !== this.props.content && this.props.content !== this.lastEmittedContent) {
			const template = this.parseContentWithFallback(this.props.content);
			const jsonText = toJson(template);
			this.lastEmittedContent = jsonText;
			this.setState({
				template,
				jsonText,
				jsonError: ""
			});
			this.emitChange(jsonText, true);
		}
	}

	getContent() {
		if (this.state.jsonError) {
			return this.state.jsonText;
		}
		return toJson(normalizeProjectOpsTemplate(this.state.template));
	}

	parseContentWithFallback(content) {
		try {
			return parseTemplateContent(content);
		} catch (err) {
			console.warn("Unable to parse Project Ops template, falling back to seed.", err);
			return getCustomHomeProjectTemplate();
		}
	}

	emitChange(content, valid = true, error = null) {
		this.lastEmittedContent = content;
		if (_.isFunction(this.props.onChange)) {
			this.props.onChange(content);
		}
		if (_.isFunction(this.props.onValidityChange)) {
			this.props.onValidityChange({ valid, error });
		}
	}

	applyTemplate(nextTemplate) {
		const template = normalizeProjectOpsTemplate(nextTemplate);
		const jsonText = toJson(template);
		this.setState({
			template,
			jsonText,
			jsonError: ""
		});
		this.emitChange(jsonText, true);
	}

	handleTemplateFieldChange(field, value) {
		this.applyTemplate({
			...this.state.template,
			[field]: value
		});
	}

	handleStageFieldChange(stageIndex, field, value) {
		const stages = [...(this.state.template.stages || [])];
		stages[stageIndex] = {
			...(stages[stageIndex] || {}),
			[field]: value
		};
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	handleStageBudgetTargetsChange(stageIndex, value) {
		this.handleStageFieldChange(stageIndex, "defaultBudgetTargets", textToBudgetTargets(value));
	}

	handleStageProjectionFieldChange(stageIndex, field, value) {
		const stages = [...(this.state.template.stages || [])];
		const stage = stages[stageIndex] || {};
		stages[stageIndex] = {
			...stage,
			projection: {
				...(stage.projection || {}),
				[field]: value
			}
		};
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	handleStageProjectionBooleanChange(stageIndex, field, checked) {
		this.handleStageProjectionFieldChange(stageIndex, field, checked === true);
	}

	handleJsonTextChange(event) {
		const jsonText = event.target.value;
		try {
			const template = normalizeProjectOpsTemplate(JSON.parse(jsonText));
			const normalizedJson = toJson(template);
			this.setState({
				template,
				jsonText,
				jsonError: ""
			});
			this.emitChange(normalizedJson, true);
		} catch (err) {
			this.setState({
				jsonText,
				jsonError: err.message
			});
			this.emitChange(jsonText, false, err);
		}
	}

	handleAddStage() {
		const stages = [...(this.state.template.stages || [])];
		const stageNumber = stages.length + 1;
		stages.push({
			key: `stage_${stageNumber}`,
			label: `Stage ${stageNumber}`,
			workItemType: PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE,
			initialStatus: "open",
			priority: "normal",
			activityRole: `stage_${stageNumber}`,
			titleTemplate: `Stage ${stageNumber} - {{projectName}}`,
			description: "",
			projection: {}
		});
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	handleRemoveStage(stageIndex) {
		const stages = [...(this.state.template.stages || [])];
		stages.splice(stageIndex, 1);
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	handleMoveStage(stageIndex, direction) {
		const stages = [...(this.state.template.stages || [])];
		const nextIndex = stageIndex + direction;
		if (nextIndex < 0 || nextIndex >= stages.length) {
			return;
		}
		const [stage] = stages.splice(stageIndex, 1);
		stages.splice(nextIndex, 0, stage);
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	handleCloneStage(stageIndex) {
		const stages = [...(this.state.template.stages || [])];
		const sourceStage = _.cloneDeep(stages[stageIndex] || {});
		const label = `${sourceStage.label || "Stage"} Copy`;
		stages.splice(stageIndex + 1, 0, {
			...sourceStage,
			key: slugify(`${sourceStage.key || "stage"}_copy_${Date.now().toString(36)}`),
			label
		});
		this.applyTemplate({
			...this.state.template,
			stages
		});
	}

	renderTextField(label, value, onChange, options = {}) {
		return (
			<FormGroup style={options.style || styles.formGroup}>
				<ControlLabel>{label}</ControlLabel>
				<FormControl
					type={options.type || "text"}
					componentClass={options.componentClass}
					rows={options.rows}
					value={value || ""}
					placeholder={options.placeholder || ""}
					onChange={(event) => onChange(event.target.value)}
				/>
			</FormGroup>
		);
	}

	renderSelectField(label, value, options, onChange) {
		return (
			<FormGroup style={styles.formGroup}>
				<ControlLabel>{label}</ControlLabel>
				<FormControl
					componentClass="select"
					value={value || ""}
					onChange={(event) => onChange(event.target.value)}
				>
					{options.map(option => {
						const optionValue = _.isPlainObject(option) ? option.value : option;
						const labelValue = _.isPlainObject(option) ? option.label : optionLabel(option);
						return <option key={`${label}-${optionValue}`} value={optionValue}>{labelValue || "None"}</option>;
					})}
				</FormControl>
			</FormGroup>
		);
	}

	renderTemplateFields() {
		const template = this.state.template || {};
		return (
			<div style={styles.section}>
				<div style={styles.sectionTitle}>Template</div>
				<div style={styles.grid}>
					{this.renderTextField("Key", template.key, value => this.handleTemplateFieldChange("key", value))}
					{this.renderTextField("Label", template.label, value => this.handleTemplateFieldChange("label", value))}
					{this.renderTextField("Project Type", template.projectType, value => this.handleTemplateFieldChange("projectType", value))}
					{this.renderSelectField("Origination", template.defaultOriginationMode, ORIGINATION_MODE_OPTIONS, value => this.handleTemplateFieldChange("defaultOriginationMode", value))}
					{this.renderSelectField("Customer Account Policy", template.defaultAccountPolicy, ACCOUNT_POLICY_OPTIONS, value => this.handleTemplateFieldChange("defaultAccountPolicy", value))}
				</div>
				{this.renderTextField("Description", template.description, value => this.handleTemplateFieldChange("description", value), {
					componentClass: "textarea",
					rows: 2,
					style: styles.formGroupWide
				})}
			</div>
		);
	}

	renderStage(stage = {}, stageIndex) {
		const projection = stage.projection || {};
		const isActivityRun = stage.workItemType === PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE;
		const budgetTargetsText = getProjectOpsTemplateBudgetTargetsText(stage);
		return (
			<div key={`project-ops-template-stage-${stageIndex}-${stage.key}`} style={styles.stageCard}>
				<div style={styles.stageHeader}>
					<div>
						<div style={styles.stageTitle}>
							{stage.label || stage.key || `Stage ${stageIndex + 1}`}
						</div>
						<div style={styles.stageSubTitle}>
							<Label bsStyle="default">{optionLabel(stage.workItemType)}</Label>{" "}
							{stage.contractMode && <Label bsStyle="primary">{optionLabel(stage.contractMode)}</Label>}{" "}
							{stage.changeMode && <Label bsStyle="warning">{optionLabel(stage.changeMode)}</Label>}
						</div>
					</div>
					<div style={styles.stageActions}>
						<Button bsSize="xsmall" onClick={() => this.handleMoveStage(stageIndex, -1)} disabled={stageIndex === 0}>
							<FontAwesomeIcon icon="arrow-up" />
						</Button>
						<Button bsSize="xsmall" onClick={() => this.handleMoveStage(stageIndex, 1)} disabled={stageIndex >= (this.state.template.stages || []).length - 1}>
							<FontAwesomeIcon icon="arrow-down" />
						</Button>
						<Button bsSize="xsmall" onClick={() => this.handleCloneStage(stageIndex)}>
							<FontAwesomeIcon icon={["far", "copy"]} />
						</Button>
						<Button bsSize="xsmall" bsStyle="danger" onClick={() => this.handleRemoveStage(stageIndex)}>
							<FontAwesomeIcon icon="trash" />
						</Button>
					</div>
				</div>

				<div style={styles.grid}>
					{this.renderTextField("Key", stage.key, value => this.handleStageFieldChange(stageIndex, "key", value))}
					{this.renderTextField("Label", stage.label, value => this.handleStageFieldChange(stageIndex, "label", value))}
					{this.renderSelectField("Work Item Type", stage.workItemType, WORK_ITEM_TYPE_OPTIONS, value => this.handleStageFieldChange(stageIndex, "workItemType", value))}
					{this.renderSelectField("Initial Status", stage.initialStatus, STATUS_OPTIONS, value => this.handleStageFieldChange(stageIndex, "initialStatus", value))}
					{this.renderSelectField("Priority", stage.priority, PRIORITY_OPTIONS, value => this.handleStageFieldChange(stageIndex, "priority", value))}
					{this.renderTextField("Activity Role", stage.activityRole, value => this.handleStageFieldChange(stageIndex, "activityRole", value))}
					{this.renderSelectField("Artifact Type", stage.artifactType, ARTIFACT_TYPE_OPTIONS, value => this.handleStageFieldChange(stageIndex, "artifactType", value))}
					{this.renderSelectField("Contract Mode", stage.contractMode, CONTRACT_MODE_OPTIONS, value => this.handleStageFieldChange(stageIndex, "contractMode", value))}
					{this.renderSelectField("Change Mode", stage.changeMode, CHANGE_MODE_OPTIONS, value => this.handleStageFieldChange(stageIndex, "changeMode", value))}
					{this.renderSelectField("Budget Policy", stage.budgetApplicationPolicy, BUDGET_POLICY_OPTIONS, value => this.handleStageFieldChange(stageIndex, "budgetApplicationPolicy", value))}
					{this.renderTextField("Addendum #", stage.addendumNumber, value => this.handleStageFieldChange(stageIndex, "addendumNumber", value), { type: "number" })}
					{this.renderTextField("Contract Document ID", stage.contractDocumentId, value => this.handleStageFieldChange(stageIndex, "contractDocumentId", value))}
					{this.renderSelectField("Milestone Stamped On Completion", stage.milestoneKey, MILESTONE_KEY_OPTIONS, value => this.handleStageFieldChange(stageIndex, "milestoneKey", value))}
				</div>

				{isActivityRun && (
					<div style={styles.grid}>
						{this.renderTextField("Activity ID Key", stage.activityIdKey, value => this.handleStageFieldChange(stageIndex, "activityIdKey", value))}
						{this.renderTextField("Default Activity ID", stage.defaultActivityId, value => this.handleStageFieldChange(stageIndex, "defaultActivityId", value))}
						{this.renderTextField("Activity ID State Field", stage.activityIdStateField, value => this.handleStageFieldChange(stageIndex, "activityIdStateField", value))}
						{this.renderTextField("Assignee State Field", stage.assigneeStateField, value => this.handleStageFieldChange(stageIndex, "assigneeStateField", value))}
						{this.renderTextField("Due State Field", stage.dueAtStateField, value => this.handleStageFieldChange(stageIndex, "dueAtStateField", value))}
						{this.renderTextField("Budget Targets State Field", stage.budgetTargetsStateField, value => this.handleStageFieldChange(stageIndex, "budgetTargetsStateField", value))}
					</div>
				)}

				{this.renderTextField("Title Template", stage.titleTemplate, value => this.handleStageFieldChange(stageIndex, "titleTemplate", value), {
					style: styles.formGroupWide
				})}
				{this.renderTextField("Description", stage.description, value => this.handleStageFieldChange(stageIndex, "description", value), {
					componentClass: "textarea",
					rows: 2,
					style: styles.formGroupWide
				})}
				{this.renderTextField("Default Budget Targets", budgetTargetsText, value => this.handleStageBudgetTargetsChange(stageIndex, value), {
					componentClass: "textarea",
					rows: 3,
					style: styles.formGroupWide,
					placeholder: "One budget target per line"
				})}

				<div style={styles.projectionBox}>
					<div style={styles.projectionTitle}>Projection / Reducer</div>
					<div style={styles.grid}>
						{this.renderTextField("Reducer Key", projection.reducerKey, value => this.handleStageProjectionFieldChange(stageIndex, "reducerKey", value))}
						<FormGroup style={styles.checkboxGroup}>
							<ControlLabel>Extract</ControlLabel>
							<Checkbox
								checked={projection.extractCustomerContact === true}
								onChange={(event) => this.handleStageProjectionBooleanChange(stageIndex, "extractCustomerContact", event.target.checked)}
							>
								Customer contact
							</Checkbox>
							<Checkbox
								checked={projection.extractPricingContract === true}
								onChange={(event) => this.handleStageProjectionBooleanChange(stageIndex, "extractPricingContract", event.target.checked)}
							>
								Pricing contract
							</Checkbox>
							<Checkbox
								checked={projection.extractBudgetFacts === true}
								onChange={(event) => this.handleStageProjectionBooleanChange(stageIndex, "extractBudgetFacts", event.target.checked)}
							>
								Budget facts
							</Checkbox>
						</FormGroup>
					</div>
				</div>
			</div>
		);
	}

	renderStages() {
		const stages = this.state.template.stages || [];
		return (
			<div style={styles.section}>
				<div style={styles.sectionHeader}>
					<div>
						<div style={styles.sectionTitle}>Stages</div>
						<div style={styles.hint}>Each stage becomes one Project Ops work item or activity run.</div>
					</div>
					<Button bsSize="small" bsStyle="primary" onClick={this.handleAddStage}>
						<FontAwesomeIcon icon="plus" /> Add Stage
					</Button>
				</div>
				{stages.length === 0 && (
					<Alert bsStyle="warning">This template has no stages yet.</Alert>
				)}
				{stages.map(this.renderStage)}
			</div>
		);
	}

	renderJsonEditor() {
		return (
			<div style={styles.section}>
				{this.state.jsonError && (
					<Alert bsStyle="danger">
						Invalid JSON: {this.state.jsonError}
					</Alert>
				)}
				<FormControl
					componentClass="textarea"
					value={this.state.jsonText}
					rows={26}
					onChange={this.handleJsonTextChange}
					style={styles.jsonTextArea}
				/>
			</div>
		);
	}

	render() {
		return (
			<div style={styles.container}>
				<Tabs activeKey={this.state.activeTab} onSelect={(activeTab) => this.setState({ activeTab })} id="project-ops-template-editor-tabs">
					<Tab eventKey="visual" title={<span><FontAwesomeIcon icon="project-diagram" /> Visual</span>}>
						{this.renderTemplateFields()}
						{this.renderStages()}
					</Tab>
					<Tab eventKey="json" title={<span><FontAwesomeIcon icon={["far", "file-code"]} /> JSON</span>}>
						{this.renderJsonEditor()}
					</Tab>
				</Tabs>
			</div>
		);
	}
}

const styles = {
	container: {
		minHeight: "620px",
		paddingBottom: "12px"
	},
	section: {
		marginTop: "14px",
		padding: "12px",
		border: "1px solid #d9e2ec",
		background: "#fff",
		borderRadius: "4px"
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "12px",
		marginBottom: "10px"
	},
	sectionTitle: {
		fontWeight: 700,
		fontSize: "16px",
		color: "#243b53"
	},
	hint: {
		fontSize: "12px",
		color: "#627d98"
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
		gap: "10px 12px",
		alignItems: "start"
	},
	formGroup: {
		marginBottom: "8px"
	},
	formGroupWide: {
		marginTop: "8px",
		marginBottom: "8px"
	},
	stageCard: {
		marginTop: "12px",
		padding: "12px",
		border: "1px solid #bcccdc",
		background: "#f8fafc",
		borderRadius: "4px"
	},
	stageHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: "12px",
		marginBottom: "10px"
	},
	stageTitle: {
		fontWeight: 700,
		fontSize: "15px",
		color: "#102a43"
	},
	stageSubTitle: {
		display: "flex",
		flexWrap: "wrap",
		gap: "5px",
		marginTop: "4px"
	},
	stageActions: {
		display: "flex",
		flexWrap: "wrap",
		gap: "5px"
	},
	projectionBox: {
		marginTop: "10px",
		padding: "10px",
		border: "1px solid #d9e2ec",
		background: "#fff",
		borderRadius: "4px"
	},
	projectionTitle: {
		fontWeight: 700,
		marginBottom: "8px",
		color: "#334e68"
	},
	checkboxGroup: {
		marginBottom: "8px"
	},
	jsonTextArea: {
		fontFamily: "Menlo, Consolas, monospace",
		fontSize: "12px",
		minHeight: "520px"
	}
};
